import { Injectable, OnDestroy } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { combineLatest, Subscription, take } from 'rxjs';

import { AppState, SentryLogger } from '@fcom/core';
import {
  GtmCustomMetrics,
  GtmEvent,
  HaulType,
  UpgradePaymentShortType,
  UpgradePaymentType,
  UpgradePurchaseData,
  UpgradePurchaseDataBasketFlight,
} from '@fcom/common';
import { DateFormat, Pattern } from '@fcom/core/utils';
import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm';

import { BookingRef, ExtendedFlightBasket, PaymentType } from '../interfaces';
import { getCabinFromUpgradeName, getPaymentFromFlight } from '../utils/data.utils';
import { getBasketFlights, getBasketPassengerIds, selectDataState } from '../store';
import { Cabin, FlightV2 } from '../api/models';
import { getLocationsForGAString, isLongHaulUpgraded } from '../utils';
import { basketHasWaitlistUpgrade } from '../utils/basket.utils';

@Injectable({
  providedIn: 'root',
})
export class UpgradeAnalyticsLegacyService implements OnDestroy {
  subscriptions: Subscription = new Subscription();
  dateFormat = 'yyyy-MM-dd';
  upgradePaymentTypeMap = {
    [Cabin.Ecopremium]: {
      [PaymentType.money]: UpgradePaymentType.ECOPREMIUM_CASH,
      [PaymentType.points]: UpgradePaymentType.ECOPREMIUM_POINTS,
      [PaymentType.EUROPE_UPGRADE]: UpgradePaymentType.ECOPREMIUM_SHORT_HAUL_VOUCHER,
      [PaymentType.LONG_HAUL_UPGRADE]: UpgradePaymentType.ECOPREMIUM_LONG_HAUL_VOUCHER,
      [PaymentType.COMPLIMENTARY]: UpgradePaymentType.ECOPREMIUM_COMPLIMENTARY,
    },
    [Cabin.Business]: {
      [PaymentType.money]: UpgradePaymentType.BUSINESS_CASH,
      [PaymentType.points]: UpgradePaymentType.BUSINESS_POINTS,
      [PaymentType.EUROPE_UPGRADE]: UpgradePaymentType.BUSINESS_SHORT_HAUL_VOUCHER,
      [PaymentType.LONG_HAUL_UPGRADE]: UpgradePaymentType.BUSINESS_LONG_HAUL_VOUCHER,
      [PaymentType.COMPLIMENTARY]: UpgradePaymentType.BUSINESS_COMPLIMENTARY,
    },
  };

  constructor(
    private store$: Store<AppState>,
    private languageService: LanguageService,
    private sentry: SentryLogger,
    private gtmService: GtmService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  sendUpgradePurchaseEvent(): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.pipe(select(getBasketFlights)),
        this.store$.pipe(select(getBasketPassengerIds)),
        this.store$.pipe(select(selectDataState)),
        this.languageService.translate('date'),
      ])
        .pipe(take(1))
        .subscribe(([flightsBasket, passengerIds, dataState, dateTranslations]) => {
          try {
            let hasPaidWithMoney = false;
            let totalPointsSpent = 0;
            let totalMoneySpent = 0;
            let haulType = HaulType.SHORT;

            flightsBasket.forEach((flight) => {
              const paymentOption = getPaymentFromFlight(flight.flight, flight.productName, flight.paymentType);

              if (flight.paymentType === PaymentType.money) {
                hasPaidWithMoney = true;

                totalMoneySpent += Number(paymentOption.price) * passengerIds.length;
              }

              if (flight.paymentType === PaymentType.points) {
                totalPointsSpent += Number(paymentOption.price) * passengerIds.length;
              }

              if (flight.flight.segmentInfo.longHaul) {
                haulType = HaulType.LONG;
              }
            });

            const event: UpgradePurchaseData = {
              upgradePurchase: {
                activeCurrency: hasPaidWithMoney ? 'EUR' : null,
                purchaseSource: dataState.source || null,
                totalPointsSpent,
                origin: dataState.summary[0].originLocationCode,
                destination: dataState.summary[0].destinationLocationCode,
                departure: new DateFormat(dateTranslations).format(
                  dataState.flights[0].segmentInfo.departureDateTime,
                  new Pattern(this.dateFormat)
                ),
                return: new DateFormat(dateTranslations).format(
                  dataState.flights[dataState.flights.length - 1].segmentInfo.arrivalDateTime,
                  new Pattern(this.dateFormat)
                ),
                haulType,
              },
              ecommerce: {
                purchase: {
                  actionField: {
                    id: this.createActionIdForJourney(
                      flightsBasket,
                      dataState.flights,
                      dataState.booking,
                      dateTranslations
                    ),
                    revenue: String(totalMoneySpent),
                  },
                  products: flightsBasket.map((flight) =>
                    this.generateBasketFlight(flight, flightsBasket, passengerIds)
                  ),
                },
              },
            };

            this.sendTrackingEvent(event);
          } catch (e) {
            this.sentry.error('Failed to create analytics event for a one-upgrade purchase', {
              e: e.message,
              stack: e.stack,
              data: JSON.stringify({
                flightsBasket,
                passengerIds,
                dataState,
                dateTranslations,
              }),
            });
          }
        })
    );
  }

  createActionIdForJourney(
    flightsBasket: ExtendedFlightBasket[],
    flights: FlightV2[],
    bookingRef: BookingRef,
    dateTranslations: Record<string, unknown>
  ): string {
    const isWaitlist = basketHasWaitlistUpgrade(flightsBasket);
    const startTime = new DateFormat(dateTranslations).format(
      flights[0].segmentInfo.departureDateTime,
      new Pattern(this.dateFormat)
    );
    const endTime = new DateFormat(dateTranslations).format(
      flights[flights.length - 1].segmentInfo.arrivalDateTime,
      new Pattern(this.dateFormat)
    );

    const locationsAndStartTime = `${getLocationsForGAString(flights)}_dep:${startTime}`;
    const bookingRefAndUpgradeStatus = `_${bookingRef.recLoc.toUpperCase()}${
      isWaitlist ? '_upgrade_waitlisted' : '_upgrade'
    }`;

    if (flights.length > 1) {
      return `${locationsAndStartTime}_ret:${endTime}${bookingRefAndUpgradeStatus}`;
    }

    return `${locationsAndStartTime}${bookingRefAndUpgradeStatus}`;
  }

  generateBasketFlight(
    flightBasket: ExtendedFlightBasket,
    flightsBasket: ExtendedFlightBasket[],
    passengerIds: string[]
  ): UpgradePurchaseDataBasketFlight {
    const cabin = getCabinFromUpgradeName(flightBasket.productName);
    let paymentType: PaymentType;
    const isComplimentary =
      !flightBasket.flight.segmentInfo.longHaul &&
      isLongHaulUpgraded(flightsBasket, flightBasket.flight.segmentInfo.group);

    if (isComplimentary) {
      paymentType = PaymentType.COMPLIMENTARY;
    } else {
      paymentType = flightBasket.paymentType;
    }

    const category = this.upgradePaymentTypeMap[cabin][paymentType];

    let price = '0';

    if (flightBasket.paymentType === PaymentType.money) {
      price = getPaymentFromFlight<'money'>(
        flightBasket.flight,
        flightBasket.productName,
        flightBasket.paymentType
      )?.price;
    }

    let points = 0;

    if (flightBasket.paymentType === PaymentType.points) {
      points = getPaymentFromFlight<'points'>(
        flightBasket.flight,
        flightBasket.productName,
        flightBasket.paymentType
      )?.price;
    }

    return {
      name: 'Upgrade',
      id: this.generateIdForFlightInBasket(flightBasket, flightsBasket),
      price,
      brand: 'Finnair',
      category,
      quantity: passengerIds.length,
      [GtmCustomMetrics.POINTS_SPENT]: points,
    };
  }

  generateIdForFlightInBasket(flightBasket: ExtendedFlightBasket, flightsBasket: ExtendedFlightBasket[]): string {
    let paymentType = UpgradePaymentShortType.UNKNOWN;

    if (
      !flightBasket.flight.segmentInfo.longHaul &&
      isLongHaulUpgraded(flightsBasket, flightBasket.flight.segmentInfo.group)
    ) {
      paymentType = UpgradePaymentShortType.COMPLIMENTARY;
    } else if (flightBasket.paymentType === PaymentType.money) {
      paymentType = UpgradePaymentShortType.CASH;
    } else if (
      flightBasket.paymentType === PaymentType.EUROPE_UPGRADE ||
      flightBasket.paymentType === PaymentType.LONG_HAUL_UPGRADE
    ) {
      paymentType = UpgradePaymentShortType.VOUCHER;
    } else if (flightBasket.paymentType === PaymentType.points) {
      paymentType = UpgradePaymentShortType.POINTS;
    }

    const boardPoint = flightBasket.flight.segmentInfo.boardPoint;
    const offPoint = flightBasket.flight.segmentInfo.offPoint;
    const longOrShortHaul = flightBasket.flight.segmentInfo.longHaul ? HaulType.LONG : HaulType.SHORT;

    return `UPGRADE_${boardPoint}-${offPoint}_${paymentType}_${longOrShortHaul}`;
  }

  sendTrackingEvent(gaEvent: UpgradePurchaseData): void {
    this.gtmService.pushEventToDataLayer(GtmEvent.UPGRADE_PURCHASE, gaEvent);
  }
}
